import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {DatePipe} from '@angular/common';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {classToClass, plainToClass} from 'class-transformer';
import {Subscription} from "rxjs";

import {GrowlService, UserService} from "@app/core";
import {DialogStyle40, DialogStyle700, LabelChangeLeaveActionType, NoTitlecaseCombinedLeaveType, YearRange} from "@app/shared/classes/common";
import {RegExp4DigitInteger2DigitFraction} from "@app/shared/classes/reg-exp";
import {Calendar} from "@app/shared/calendar/calendar";
import {QueryResult} from "@app/shared/classes/query-result";
import {UserInfo} from "@app/shared/classes/user";

import {StringReplacePipe} from "@app/shared/pipes/string-replace.pipe";
import {TitleCasePipe} from "@app/shared/pipes/title-case.pipe";
import {Format} from "@app/shared/enums/format.enum";
import {Validation} from "@app/shared/enums/validation.enum";
import {GrowlType} from "@app/shared/enums/growl-type.enum";
import {TimesheetTransactionYear} from "@app/shared/enums/timesheet-transaction-year.enum";

import {CommonTransformationService} from "@app/shared/services/common-transformation.service";
import {CalendarService} from "@app/shared/services/calendar.service";
import {DateService} from "@app/shared/services/date.service";
import {CombinedLeaveType, LeaveActionType, LeaveDetail, LeaveHistory, LeaveHistoryRequest, LeaveHistoryUserType} from '../classes/leave-history';
import {LeaveHistoryDataService} from '../leave-history/leave-history-data.service';
import {CalendarType, CommonDataService, IdNamePair, Roles} from "@app/shared";
import {format} from "date-fns";
import {forkJoin} from "rxjs/internal/observable/forkJoin";


@Component({
  selector: 'app-leave-history',
  templateUrl: './leave-history.component.html',
  styleUrls: ['./leave-history.component.css'],
  providers: [LeaveHistoryDataService, DatePipe, StringReplacePipe, TitleCasePipe, ConfirmationService],
})

export class LeaveHistoryComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  request: LeaveHistoryRequest;

  @Input()
  allowManualEdit: boolean;           // if true and user has appropriate role (HR Generalist), allow user to add/update/delete manual adjustment

  DialogStyle40 = DialogStyle40;
  DialogStyle700 = DialogStyle700;
  YearRange = YearRange;
  RegExp4DigitWhole2DigitFraction = RegExp4DigitInteger2DigitFraction;
  Format: typeof Format = Format;
  Roles: typeof Roles = Roles;
  Validation: typeof Validation = Validation;
  CombinedLeaveType: typeof CombinedLeaveType = CombinedLeaveType;
  LeaveActionType: typeof LeaveActionType = LeaveActionType;
  LeaveHistoryUserType: typeof LeaveHistoryUserType = LeaveHistoryUserType;

  NoTitlecaseCombinedLeaveType = NoTitlecaseCombinedLeaveType;
  LabelChangeLeaveActionType = LabelChangeLeaveActionType;

  leaveHistories: LeaveHistory[];             // for request.userType == Manager, this array contains leave history of subordinates
                                              // for request.userType == Self or User, this array contains only one item - self/user's leave history
  selectedLeaveHistory: LeaveHistory[];       // array with selected leave history in it, so that it can be shown on the table
  leaveHistoryToEdit: LeaveHistory;
  combinedPool: boolean = false;              // if true, show PTO else show Sick and Vacation
  leaveHistoryInitialized: boolean = false;   // true if retrieved leave history has id

  yearDropdown: SelectItem[];                 // dropdown menu created using the current date. it should contain current year and a few years back
  leaveDetailsLengths = [];                   // a list containing number of leave details by leaveType

  userSubordinateSub: Subscription;           // subscription to get subordinates for the user; only relevant for request.leaveType == Manager
  selectedEmployee: UserInfo;                 // selected subordinate for manager
  subordinateDropdown: SelectItem[];          // a list of user's subordinates; only applicable if the request type is Manager

  displayLeaveHistoryDialog: boolean;         // if true, show the leave history in a popup dialog

  displayBeginningBalanceDialog: boolean;

  selectedLeaveType: CombinedLeaveType;
  selectedLeaveDetails: LeaveDetail[];
  displayLeaveDetailsDialog: boolean;         // a dialog contains leave details

  isLeaveAdjustmentAllowed: boolean;          // set to true if the requestType is User and the user has the HR Generalist role
  leaveAdjustmentToEdit: LeaveDetail;         // temporary object to hold manual adjustment
  leaveTypeDropdown: SelectItem[];            // required dropdown in the manual adjustment dialog
  actionTypeDropdown: SelectItem[];           // allow change to beginning balance or adjustment
  adjustmentOptions = [{label: "Yes", value: true}, {label: "No", value: false}];

  minLeaveOn: Date;
  maxLeaveOn: Date;
  timesheetPeriods: Calendar[];                 // retrieved from the backend to build the dropdown and also used to set min/max fields
  timesheetPeriodDropdown: SelectItem[] = [];   // optional dropdown in the manual adjustment dialog
  newLeaveAdjustmentTooltip: string;
  showLeaveAdjustmentDialog: boolean = false;    // if true, show the dialog to add/update manual leave adjustment
  leaveAdjustmentDialogHeader: string;          // leave adjustment / beginning balance dialog


  constructor(private transform: CommonTransformationService, private calendarService: CalendarService, private datePipe: DatePipe,
              public user: UserService, private growl: GrowlService, private confirmationService: ConfirmationService,
              private stringReplacePipe: StringReplacePipe, private titleCasePipe: TitleCasePipe, private commonDataService: CommonDataService,
              private dateService: DateService, private dataService: LeaveHistoryDataService) {

    // set the subordinate dropdown subscribing to the user service; this happens when routed directly to this component
    this.userSubordinateSub = UserService.userSubordinateSub.subscribe(auth => {
      if (this.request && this.request.userType === LeaveHistoryUserType.Manager) {
        this.subordinateDropdown = this.user.resourceManagerSubordinates;
      }
    });
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.userSubordinateSub.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {

    // return if there is no request input object
    if (!this.request) {
      return;
    }
    // default to current year
    if (!this.request.year) {
      this.request.year = String(new Date().getFullYear());
    }

    this.getYearMenu(this.request.userId, this.request.userType);
    this.buildActionTypeDropdown();

    // show name column if leave history is for one's direct report
    if (this.request.userType === LeaveHistoryUserType.Manager) {
      this.subordinateDropdown = this.user.resourceManagerSubordinates;
    } else {
      if (this.allowManualEdit && this.request.userType == LeaveHistoryUserType.User && this.user.hasRole[Roles.HrGeneralist]) {
        this.isLeaveAdjustmentAllowed = true;
        this.buildTimesheetPeriodDropdown(this.request.year);
      }
    }

    if ((this.request.userType != LeaveHistoryUserType.User) || (this.request.userType == LeaveHistoryUserType.User && this.request.userId)) {
      this.getLeaveHistory(this.request);
    } else {
      console.warn("cannot get leave history for unspecified user");
    }
  }


  /**
   * @desc calculate number of different leave details based on the leave type; this array is used to show or not show the detail dialog
   *       this method should be called after retrieving leave history from the backend
   */
  getNumberOfLeaveDetails(leaveDetails: LeaveDetail[]): void {
    this.leaveDetailsLengths = [];
    for (const leaveType in CombinedLeaveType) {
      this.leaveDetailsLengths[CombinedLeaveType[leaveType]] = this.getNumberOfLeaveDetailByType(leaveDetails, CombinedLeaveType[leaveType.toString()]);
    }
  }

  onChangeEmployee(selectedEmployee: UserInfo): void {

    const selectedLeaveHistory = this.leaveHistories.find(lh => lh.userId == selectedEmployee.userId);
    if (selectedLeaveHistory) {
      this.combinedPool = selectedLeaveHistory.combinedPool;
      this.selectedLeaveHistory = [selectedLeaveHistory];
      this.getNumberOfLeaveDetails(this.selectedLeaveHistory[0].employeeLeaveDetails);
    } else {
      this.selectedLeaveHistory = null;
    }
  }

  /**
   * @desc retrieve the leave history from the backend and process the data based on the request type
   */
  getLeaveHistory(request: LeaveHistoryRequest, selectedLeaveType?: CombinedLeaveType): void {
    if (request) {  // check whether request is null in case the component is called without input request
      this.leaveHistories = [];

      this.dataService.getLeaveHistory(request.userType, request.userId, request.year).subscribe(res => {
        this.processLeaveHistory(res, request.userType, selectedLeaveType);
      });
    } else {
      console.warn("getLeaveHistory called with empty request");
    }
  }

  /**
   * @desc display the leave detail on popup dialog based on the leaveType
   */
  showLeaveDetailsByType(leave: LeaveHistory, leaveType: CombinedLeaveType): void {
    this.selectedLeaveType = leaveType;
    this.selectedLeaveDetails = this.getLeaveDetailsByType(leave.employeeLeaveDetails, leaveType);
    this.displayLeaveDetailsDialog = true;
  }

  onClickInitBeginningBalance(leaveHistory: LeaveHistory): void {
    this.leaveHistoryToEdit = classToClass(leaveHistory);
    this.displayBeginningBalanceDialog = true;
  }

  onClickSaveBeginningBalance(leaveHistory: LeaveHistory): void {
    this.dataService.saveLeaveHistory(leaveHistory).subscribe(res => {
      // ToDo jc: do i need to pass selectedLeaveType?
      this.processLeaveHistory(plainToClass(LeaveHistory, res), LeaveHistoryUserType.User);
    });
    this.displayBeginningBalanceDialog = false;
  }

  /**
   * @desc create a new manual adjustment entry to be populated
   */
  onClickAddLeaveAdjustment(leaveHistory: LeaveHistory): void {
    this.leaveAdjustmentToEdit = new LeaveDetail();
    this.leaveAdjustmentToEdit.leaveHistoryId = leaveHistory.id;
    this.leaveAdjustmentToEdit.actionType = LeaveActionType.Adjustment;
    this.leaveAdjustmentDialogHeader = "Add Leave Adjustment";
    this.onChangeLeaveActionType(leaveHistory, this.leaveAdjustmentToEdit);
    this.showLeaveAdjustmentDialog = true;
  }

  onClickAddBeginningBalance(leaveHistory: LeaveHistory): void {
    this.leaveAdjustmentToEdit = new LeaveDetail();
    this.leaveAdjustmentToEdit.leaveHistoryId = leaveHistory.id;
    this.leaveAdjustmentToEdit.actionType = LeaveActionType.BegBalance;
    this.leaveAdjustmentToEdit.actionOn = this.datePipe.transform(new Date(), Format.DataserviceDate);
    this.leaveAdjustmentDialogHeader = "Add Beginning Balance";
    this.onChangeLeaveActionType(leaveHistory, this.leaveAdjustmentToEdit);
    this.showLeaveAdjustmentDialog = true;
  }

  onClickEditLeaveDetail(combinedPool: boolean, leaveDetail: LeaveDetail): void {
    this.leaveAdjustmentToEdit = classToClass(leaveDetail);
    this.buildLeaveTypeDropdown(combinedPool, leaveDetail.actionType);
    this.showLeaveAdjustmentDialog = true;
  }

  /**
   * @desc delete the manual leave adjustment in the backend,
   *       and then update the frontend tables by re-retrieving the leave history in order to avoid re-calculating all the leave balance in the frontend
   */
  onClickDeleteLeaveDetail(leaveDetail: LeaveDetail): void {
    const leaveType = this.titleCasePipe.transform(leaveDetail.leaveType, NoTitlecaseCombinedLeaveType);
    this.confirmationService.confirm({
      message: `Do you want to delete this manual leave adjustment? ${leaveDetail.hours} ${leaveType} leave hours on ${leaveDetail.actionOn}`,
      header: 'Delete Confirmation',
      icon: 'fa fa-trash',
      accept: () => {
        this.dataService.deleteLeaveDetail(leaveDetail.id).subscribe(res => {
          this.getLeaveHistory(this.request, this.selectedLeaveType);
          this.growl.add(GrowlType.Success, "Employee leave adjustment deleted.");
        });
      }
    });
  }

  onChangeLeaveActionType(leaveHistory: LeaveHistory, leaveAdjustment: LeaveDetail): void {
    this.buildLeaveTypeDropdown(leaveHistory.combinedPool, leaveAdjustment.actionType, leaveHistory.employeeLeaveDetails);
    if (leaveAdjustment.actionType == LeaveActionType.BegBalance) {
      leaveAdjustment.timesheetPeriodId = null;
    }
  }

  onChangeTimesheetPeriod(timesheetPeriodId: number): void {
    const timesheetPeriod = this.timesheetPeriods.find(tp => tp.id == timesheetPeriodId);
    if (timesheetPeriod) {
      this.minLeaveOn = this.dateService.convertStringDateToPCalendarDate(timesheetPeriod.start);
      this.maxLeaveOn = this.dateService.convertStringDateToPCalendarDate(timesheetPeriod.end);
      this.leaveAdjustmentToEdit.actionOn = timesheetPeriod.end;
    } else {
      this.minLeaveOn = null;
      this.maxLeaveOn = null;
      this.leaveAdjustmentToEdit.actionOn = null;
    }
  }

  /**
   * @desc save or update the manual leave adjustment in the backend,
   *       and then update the frontend tables by re-retrieving the leave history in order to avoid re-calculating all the leave balance in the frontend
   */
  onSaveLeaveDetail(leaveDetail: LeaveDetail): void {
    const leaveDetailId = leaveDetail.id;
    this.dataService.saveOrUpdateLeaveDetail(leaveDetail).subscribe(res => {

      this.getLeaveHistory(this.request, this.selectedLeaveType); // get the recalculated data from the backend; avoid duplicate code in frontend

      if (leaveDetailId) {
        this.growl.add(GrowlType.Success, "Employee leave adjustment updated.");
      } else {
        this.growl.add(GrowlType.Success, "New employee adjustment leave added.");
      }
    });
    this.showLeaveAdjustmentDialog = false;
  }

  private getYearMenu(userId: number, userType: LeaveHistoryUserType): void {
    const date = format(new Date(), Format.FnsDataserviceDate);
    if (userId && userType == LeaveHistoryUserType.User) {
      forkJoin([
        this.calendarService.getCalendarYears(CalendarType.Timesheet),
        this.commonDataService.getUserById(userId)
      ]).subscribe(([year, userSummary]) => {
        this.yearDropdown = [];
        let years: IdNamePair[] = year;
        years.forEach(y => {
          this.yearDropdown.push({label: String(y.name), value: String(y.name)});
        });
        // const yearsInMenu = Number(year) - Number(userSummary.dateOfHire.split("-")[0]) + 1;
        // this.yearDropdown = [];
        // for (let i = 0; i < yearsInMenu; i++) {
        //   this.yearDropdown.push({label: String(Number(year) - i), value: String(Number(year) - i)});
        // }
      });
    } else {
      this.calendarService.getCalendarYears(CalendarType.Timesheet).subscribe(res => {
        this.yearDropdown = [];
        // for (let i = 0; i < TimesheetTransactionYear.listOfYear; i++) {
        //   this.yearDropdown.push({label: String(Number(res) - i), value: String(Number(res) - i)});
        // }
        let years: IdNamePair[] = res;
        years.forEach(y => {
          this.yearDropdown.push({label: String(y.name), value: String(y.name)});
        });
      });
    }
  }

  private buildTimesheetPeriodDropdown(year: string): void {
    this.dataService.getTimesheetPeriodsByYear(year).subscribe(res => {
      this.timesheetPeriods = plainToClass(Calendar, res.list);
      this.timesheetPeriodDropdown = this.dataService.convertTimesheetPeriodToDropdown(this.timesheetPeriods);
    });
  }

  private buildLeaveTypeDropdown(combinedPool: boolean, leaveActionType: LeaveActionType, leaveDetails?: LeaveDetail[]): void {

    if (leaveActionType == LeaveActionType.Adjustment) {
      this.leaveTypeDropdown = this.transform.convertEnumToDropDown(CombinedLeaveType, true, NoTitlecaseCombinedLeaveType);
      if (combinedPool) {
        this.leaveTypeDropdown = this.leaveTypeDropdown.filter(leaveType => leaveType.value != CombinedLeaveType.Sick && leaveType.value != CombinedLeaveType.Vacation);
      } else {
        this.leaveTypeDropdown = this.leaveTypeDropdown.filter(leaveType => leaveType.value != CombinedLeaveType.PTO);
      }
    } else if (leaveActionType == LeaveActionType.BegBalance) {
      if (combinedPool) {
        this.leaveTypeDropdown = [{label: CombinedLeaveType.PTO, value: CombinedLeaveType.PTO}];
        if (this.leaveAdjustmentToEdit) { // set default leave type since there is one leave type
          this.leaveAdjustmentToEdit.leaveType = CombinedLeaveType.PTO;
        }
      } else {
        let sickBeginningBalanceExists = false;
        let vacBeginningBalanceExists = false;
        if (leaveDetails) {
          leaveDetails.forEach(leaveDetail => {
            if (leaveDetail.actionType == LeaveActionType.BegBalance) {
              if (leaveDetail.leaveType == CombinedLeaveType.Sick) {
                sickBeginningBalanceExists = true;
              } else if (leaveDetail.leaveType == CombinedLeaveType.Vacation) {
                vacBeginningBalanceExists = true;
              }
            }
          });
        }
        this.leaveTypeDropdown = [];
        if (!sickBeginningBalanceExists) {
          this.leaveTypeDropdown.push({label: this.titleCasePipe.transform(CombinedLeaveType.Sick), value: CombinedLeaveType.Sick});
        }
        if (!vacBeginningBalanceExists) {
          this.leaveTypeDropdown.push({label: this.titleCasePipe.transform(CombinedLeaveType.Vacation), value: CombinedLeaveType.Vacation});
        }
        if (this.leaveTypeDropdown.length == 1 && this.leaveAdjustmentToEdit) {
          this.leaveAdjustmentToEdit.leaveType = <CombinedLeaveType>this.leaveTypeDropdown[0].value;
        }
      }
    }
  }

  private buildActionTypeDropdown(): void {
    this.actionTypeDropdown = [
      {label: this.stringReplacePipe.transform(LeaveActionType.BegBalance, LabelChangeLeaveActionType), value: LeaveActionType.BegBalance},
      {label: this.titleCasePipe.transform(LeaveActionType.Adjustment), value: LeaveActionType.Adjustment}];
  }

  private getLeaveDetailsByType(leaveDetails: LeaveDetail[], leaveType: CombinedLeaveType) {
    return leaveDetails.filter(leave => leave.leaveType == leaveType);
  }

  private getNumberOfLeaveDetailByType(leaveDetails: LeaveDetail[], leaveType: CombinedLeaveType): number {
    let count = 0;
    if (leaveDetails) {
      leaveDetails.forEach(leave => {
        if (leave.leaveType == leaveType) {
          count++;
        }
      });
    }
    return count;
  }

  private processLeaveHistory(res: QueryResult | LeaveHistory, userType: LeaveHistoryUserType, selectedLeaveType?: CombinedLeaveType) {

    if (userType === LeaveHistoryUserType.Manager) {
      this.leaveHistories = plainToClass(LeaveHistory, (<QueryResult>res).list);

      // if an employee is already selected and leave history for a different year is chosen, then find the user's leave history
      if (this.selectedEmployee) {
        this.onChangeEmployee(this.selectedEmployee);
      } else {
        this.selectedLeaveHistory = null;
      }
    } else {  // for userType Self or User
      // returns a leave history object for self or specified user
      const leaveHistory = plainToClass(LeaveHistory, res as LeaveHistory);
      this.getNumberOfLeaveDetails(leaveHistory.employeeLeaveDetails);

      // manual add/update of leave allowed only if the balance is initialized => employee leave history has id for the year
      this.leaveHistoryInitialized = leaveHistory.id > 0;
      if (this.leaveHistoryInitialized) {
        this.newLeaveAdjustmentTooltip = "New Leave Adjustment";
      } else {
        this.newLeaveAdjustmentTooltip = "Leave History must be initialized before new adjustment can be made.";
      }
      this.leaveHistories = [leaveHistory];
      this.selectedLeaveHistory = this.leaveHistories;
    }

    // if combinedPool is true, then vacation and sick balances will not be shown
    if (this.selectedLeaveHistory) {
      this.combinedPool = this.selectedLeaveHistory[0].combinedPool;
    }

    // handle manual adjustment
    if (userType === LeaveHistoryUserType.User && this.user.hasRole[Roles.HrGeneralist]) {
      this.selectedLeaveHistory = this.leaveHistories;
      if (selectedLeaveType) {
        this.selectedLeaveDetails = this.getLeaveDetailsByType(this.selectedLeaveHistory[0].employeeLeaveDetails, selectedLeaveType);
      }
    } else {
      this.displayLeaveHistoryDialog = this.request.displayOnDialog;
    }
  }
}
